// Long-lived stdio engine for the GUI sidecar.
//
// Spawned once by the Rust shell, this keeps the Node runtime + cai modules warm
// and answers newline-delimited JSON requests on stdin with JSON responses on
// stdout — eliminating a Node cold start per tab load / refresh. Each request
// carries an explicit project dir, so there is no dependency on process cwd.
//
// Protocol (one JSON object per line):
//   →  {"id":N,"cmd":"check|suggest|watchdog|health|timeline","dir":"/abs","session":"..."}
//   ←  {"id":N,"ok":true,"result":<value>}   |   {"id":N,"ok":false,"error":"msg"}
//
// Results are byte-for-byte the same data the one-shot `cai <cmd> --json` paths
// return, so the Rust client and the frontend contracts consume them unchanged.

import { createInterface } from "node:readline";
import { findConfig } from "./config.js";

interface ServerRequest {
  id: number;
  cmd: string;
  dir?: string;
  session?: string | null;
  days?: number;
}

/** Dispatch one request to the matching pure engine and return its value. */
async function handle(req: ServerRequest): Promise<unknown> {
  const config = findConfig(req.dir);
  switch (req.cmd) {
    case "check": {
      const { runDriftCheck } = await import("./drift/index.js");
      const report = await runDriftCheck(config);
      return { project_dir: config.projectRoot, report };
    }
    case "suggest": {
      const { runSuggest } = await import("./suggest/index.js");
      const report = await runSuggest(config);
      return { project_dir: config.projectRoot, report };
    }
    case "health": {
      const { runHealth } = await import("./health.js");
      return await runHealth(config);
    }
    case "timeline": {
      const { runTimeline } = await import("./timeline.js");
      return runTimeline(config, {});
    }
    case "watchdog": {
      const { checkWatchdog, serializeWatchdog } = await import("./watchdog/index.js");
      const { bridgePath, discoverFreshBridge } = await import("./watchdog/signal.js");
      const bridge = req.session ? bridgePath(req.session) : discoverFreshBridge(config.projectRoot);
      return serializeWatchdog(checkWatchdog(config, { bridgePath: bridge }));
    }
    case "stats": {
      const { readQueries, aggregateByFile, aggregateByTool } = await import("./telemetry/query-log.js");
      const days = typeof req.days === "number" ? req.days : 7;
      const sinceMs = Date.now() - days * 86_400_000;
      const queries = readQueries(config.projectRoot, { sinceMs });
      return {
        days,
        totalQueries: queries.length,
        byFile: aggregateByFile(queries),
        byTool: aggregateByTool(queries),
      };
    }
    default:
      throw new Error(`unknown cmd: ${req.cmd}`);
  }
}

export async function runGuiServer(): Promise<void> {
  // Keep stdout pure protocol: route any stray library logging to stderr.
  const origLog = console.log;
  console.log = (...args: unknown[]) => process.stderr.write(args.map(String).join(" ") + "\n");
  void origLog;

  const write = (obj: unknown) => process.stdout.write(JSON.stringify(obj) + "\n");
  const rl = createInterface({ input: process.stdin });

  for await (const line of rl) {
    const text = line.trim();
    if (!text) continue;
    let req: ServerRequest;
    try {
      req = JSON.parse(text) as ServerRequest;
    } catch {
      continue; // ignore malformed lines rather than break the stream
    }
    try {
      const result = await handle(req);
      write({ id: req.id, ok: true, result });
    } catch (err) {
      write({ id: req.id, ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }
}
